import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { formatDate } from "../utils/dateUtils";
import NavBar from "../components/NavBar";
import { RefreshIcon, ChevronLeftIcon } from "../components/icons";
import EditUserModal from "../components/EditUserModal";

const ArchiveIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <polyline points="21 8 21 21 3 21 3 8" />
    <rect x="1" y="3" width="22" height="5" />
    <line x1="10" y1="12" x2="14" y2="12" />
  </svg>
);

export default function DeactivatedProfiles() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest("[data-kebab]")) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);
  const navigate = useNavigate();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      // /profiles now returns { data, pagination }. Older deploys may
      // still return a bare array — handle both shapes defensively.
      const response = await apiFetch("/profiles?status=nonactive&limit=100");
      const list = Array.isArray(response)
        ? response
        : response?.data || [];
      setUsers(list);
    } catch (err) {
      console.error("Failed to fetch deactivated users:", err);
      setBanner({ type: "error", text: err.message || "Unable to load deactivated users" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const toggleStatus = async (id, newStatus) => {
    try {
      await apiFetch(`/profiles/${id}`, {
        method: "PUT",
        body: JSON.stringify({ status: newStatus }),
      });
      setBanner({ type: "success", text: `Profile ${newStatus === "active" ? "activated" : "deactivated"}.` });
      await fetchUsers();
    } catch (err) {
      console.error("Status update failed:", err);
      setBanner({ type: "error", text: err.message || "Failed to update status" });
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="relative p-6 space-y-6">
        {banner && (
          <div className={`flex items-start justify-between gap-4 rounded-xl border px-4 py-3 text-sm shadow-sm ${banner.type === "success" ? "border-[var(--color-notice-border)] bg-[var(--color-notice-bg)] text-[var(--color-notice-text)]" : "border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] text-[var(--color-danger-text)]"}`}>
            <span>{banner.text}</span>
            <button className="text-xs font-semibold uppercase tracking-wide" onClick={() => setBanner(null)}>Dismiss</button>
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg">
          <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] px-6 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
                <ArchiveIcon className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                  Deactivated Profiles
                </h2>
                <p className="text-xs text-[var(--color-text-muted)]">
                  Profiles that are not active will appear here.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/bureau/dashboard')}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] px-4 py-2 text-sm font-semibold transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Back to Active
              </button>
              <button
                onClick={() => fetchUsers()}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-2 text-sm font-semibold transition-colors"
              >
                <RefreshIcon className="w-4 h-4" />
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="p-6">
            {!loading && users.length === 0 && (
              <div className="text-center text-[var(--color-text-muted)] py-10">No deactivated profiles found.</div>
            )}

            {loading && (
              <div className="text-center text-[var(--color-text-muted)] py-10">Loading...</div>
            )}

            <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {users.map((u) => (
                <div key={u._id} className="relative overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
                  <div className="absolute top-3 right-3 z-10" data-kebab={u._id}>
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === u._id ? null : u._id);
                        }}
                        aria-haspopup="true"
                        aria-expanded={openMenuId === u._id}
                        className="h-8 w-8 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] flex items-center justify-center shadow-sm hover:scale-105"
                        title="Options"
                      >
                        ⋮
                      </button>

                      {openMenuId === u._id && (
                        <div role="menu" className="absolute right-0 mt-2 w-44 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg">
                          <button role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditUser(u);
                              setOpenMenuId(null);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--color-surface-hover)]"
                          >
                            Edit
                          </button>
                          <button role="menuitem"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm('Activate this profile?')) return;
                              await toggleStatus(u._id, 'active');
                              setOpenMenuId(null);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--color-surface-hover)]"
                          >
                            Activate
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-2 font-semibold text-[var(--color-text-strong)] text-lg">{u.fullName}</div>
                  <div className="text-sm text-[var(--color-text)] mb-2">{u.age ?? '—'} • {u.gender} • {u.city}</div>

                  <div className="mt-3 text-sm text-[var(--color-text)]">
                    <div>Contact: {u.contactNumber}</div>
                    <div className="mt-2 text-xs text-gray-400">Added: {formatDate(u.createdAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {editUser && <EditUserModal isOpen={!!editUser} initialData={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); fetchUsers(); }} />}
    </div>
  );
}
